import { useState } from "react";
import { useConnectionRecoveryOptional } from "@/lib/connection-recovery";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RefreshCw, Clock, Send, Loader2, WifiOff, CheckCircle } from "lucide-react";

export function ConnectionRecoveryModal() {
  const recovery = useConnectionRecoveryOptional();
  const [showReportForm, setShowReportForm] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [reportSent, setReportSent] = useState(false);

  if (!recovery) {
    return null;
  }

  const { 
    showRecoveryModal, 
    setShowRecoveryModal,
    handleRefresh, 
    handleWaitLonger, 
    handleSendReport,
    timeSinceResume,
    isRecovering,
  } = recovery;

  const handleSendReportClick = async () => {
    setIsSending(true);
    try {
      await handleSendReport(additionalInfo || undefined);
      setReportSent(true);
      setTimeout(() => {
        setShowReportForm(false);
        setReportSent(false);
        setAdditionalInfo("");
      }, 2000);
    } catch (error) {
      console.error("Failed to send report:", error);
    } finally {
      setIsSending(false);
    }
  };

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <AlertDialog open={showRecoveryModal} onOpenChange={setShowRecoveryModal}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
              <WifiOff className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <AlertDialogTitle>Connection Issue</AlertDialogTitle>
              <p className="text-sm text-muted-foreground">
                Loading for {formatTime(timeSinceResume)}
              </p>
            </div>
          </div>
          <AlertDialogDescription className="text-left">
            The app is having trouble loading data. This can happen when the app has been 
            in the background for a while.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {showReportForm ? (
          <div className="space-y-4 py-2">
            {reportSent ? (
              <div className="flex flex-col items-center py-4">
                <CheckCircle className="h-12 w-12 text-green-500 mb-2" />
                <p className="text-sm font-medium">Report sent successfully</p>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="additional-info">
                    What were you doing when this happened? (optional)
                  </Label>
                  <Textarea
                    id="additional-info"
                    value={additionalInfo}
                    onChange={(e) => setAdditionalInfo(e.target.value)}
                    placeholder="e.g., I was viewing the roster when the app went to background..."
                    rows={3}
                    data-testid="input-crash-report"
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setShowReportForm(false)}
                    disabled={isSending}
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1"
                    onClick={handleSendReportClick}
                    disabled={isSending}
                    data-testid="button-submit-report"
                  >
                    {isSending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Report
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : (
          <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
            <Button 
              onClick={handleRefresh} 
              className="w-full"
              disabled={isRecovering}
              data-testid="button-refresh-app"
            >
              {isRecovering ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh App
                </>
              )}
            </Button>
            
            <Button 
              variant="outline" 
              onClick={handleWaitLonger}
              className="w-full"
              data-testid="button-wait-longer"
            >
              <Clock className="h-4 w-4 mr-2" />
              Wait Longer
            </Button>
            
            <Button 
              variant="ghost" 
              onClick={() => setShowReportForm(true)}
              className="w-full text-muted-foreground"
              data-testid="button-send-report"
            >
              <Send className="h-4 w-4 mr-2" />
              Send Crash Report
            </Button>
          </AlertDialogFooter>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
